import { isEmpty } from '@jjj/components';
import { IChangePropertyPayload, IField } from '@dash/editForm';
import { keyBy } from 'src/helpers/core';
import actionsTypes from './actionTypes';
import { Attribute, IAddCreditState, IChangeModalVisibilityPayload, IFieldTypes } from './types';
import { createReducer, getError } from '../../../../../../../../helpers/reducer';
import { t } from '../../../../../../../../i18n';

function validate(attr: Attribute, value: IFieldTypes) {
  let errorMessage = '';

  const requiredAttributes: Attribute[] = ['name', 'description', 'value'];

  if (requiredAttributes.includes(attr)) {
    if (isEmpty(value)) {
      errorMessage = t('dash.validation.required_error', {
        fieldName: t(`dash.units.unit.credits.${attr}_label`),
      });
    }
  }

  return errorMessage;
}

const getInitialField = (fieldName: Attribute, value: string | null = null) => ({
  name: fieldName,
  value,
  touched: false,
  error: validate(fieldName, value || ''),
});

const initialEditForm = {
  name: getInitialField('name'),
  description: getInitialField('description'),
  value: getInitialField('value'),
};

const initState: IAddCreditState = {
  isLoading: false,
  editForm: initialEditForm,
  isModalVisible: false,
  isEditMode: false,
  editAction: undefined,
};

function onChangeProperty(
  state: IAddCreditState,
  { payload }: { payload: IChangePropertyPayload<Attribute, IFieldTypes> },
): IAddCreditState {
  const { attr, value } = payload;
  const { editForm } = state;

  const getValue = (val: IFieldTypes, attribute: Attribute) => ({
    value: val,
    error: validate(attribute, val),
    name: attribute,
    touched: true,
  });

  return {
    ...state,
    editForm: {
      ...editForm,
      [attr]: getValue(value, attr),
    },
  };
}

function onAddCreditRequest(state: IAddCreditState): IAddCreditState {
  return {
    ...state,
    isLoading: true,
  };
}

function onAddCreditSuccess(state: IAddCreditState): IAddCreditState {
  return {
    ...state,
    isLoading: false,
    isModalVisible: false,
    editForm: {
      name: getInitialField('name'),
      description: getInitialField('description'),
      value: getInitialField('value'),
    },
  };
}

function onAddCreditError(
  state: IAddCreditState,
  { payload }: { payload: { data: { [key: string]: string | string[] } } },
): IAddCreditState {
  const { data } = payload;

  const propsArray = Object.entries(state.editForm);
  const editForm = keyBy(
    propsArray,
    (entry: [Attribute, IField]) => {
      return entry[0];
    },
    (entry: [Attribute, IField]) => {
      const [, value] = entry;

      const errorMessage = data.value_cents ? { value: data.value_cents } : data;

      return {
        ...value,
        error: getError(entry[0], errorMessage),
        touched: true,
      };
    },
  );

  return {
    ...state,
    isLoading: false,
    editForm,
  };
}

function onProcessActionRequest(state: IAddCreditState) {
  return {
    ...state,
    isLoading: true,
  };
}

function onChangeModalVisibility(state: IAddCreditState, { payload }: { payload: IChangeModalVisibilityPayload }) {
  const { editForm } = state;
  let updatedActions;

  if (payload?.isEditMode && payload?.credit) {
    const { name, description, value, actions } = payload.credit;

    editForm.name = getInitialField('name', name);
    editForm.description = getInitialField('description', description);
    editForm.value = getInitialField('value', `${value}`);

    updatedActions = actions.find(action => action.key === 'update');
  }

  const isModalVisible = !state.isModalVisible;

  const editFormToUpdate = !isModalVisible
    ? {
        name: getInitialField('name'),
        description: getInitialField('description'),
        value: getInitialField('value'),
      }
    : editForm;

  return {
    ...state,
    isModalVisible,
    isEditMode: !!payload?.isEditMode,
    editForm: editFormToUpdate,
    editAction: updatedActions || state.editAction,
  };
}

export default createReducer(initState, {
  [actionsTypes.CHANGE_PROPERTY]: onChangeProperty,
  [actionsTypes.ADD_CREDIT_REQUEST]: onAddCreditRequest,
  [actionsTypes.ADD_CREDIT_SUCCESS]: onAddCreditSuccess,
  [actionsTypes.ADD_CREDIT_ERROR]: onAddCreditError,
  [actionsTypes.PROCESS_ACTION_REQUEST]: onProcessActionRequest,
  [actionsTypes.CHANGE_MODAL_VISIBILITY]: onChangeModalVisibility,
});
